import { useUserContextPreferences } from '../../../../../hooks/useUserContextPreferences';
import { useGetCatalog } from '../../../../../services/modules/catalogs/catalogs.queries';
import { CatalogSectionSortCriterionEnum } from '../../../enums/catalogSectionSortCriterion.enum';
import {
  CatalogSortParams,
  ResolveSortParamsParams,
  ResolveSortTargetIdParams,
  UseCatalogSortParamsParams,
  UseCatalogSortParamsResult,
} from './useCatalogSortParams.types';

const NO_SORT_PARAMS: CatalogSortParams = {};

const resolveSortTargetId = ({
  criterion,
  preference,
  catalogTargetId,
}: ResolveSortTargetIdParams): string | null => {
  if (criterion !== CatalogSectionSortCriterionEnum.CLASSIFICATION_TYPE) {
    return null;
  }

  if (preference?.sortCriterion) {
    return preference.sortTargetId ?? null;
  }

  return catalogTargetId;
};

const resolveSortParams = ({
  preference,
  catalogCriterion,
  catalogTargetId,
}: ResolveSortParamsParams): CatalogSortParams => {
  const criterion = preference?.sortCriterion ?? catalogCriterion;
  const targetId = resolveSortTargetId({ criterion, preference, catalogTargetId });

  if (!criterion || (criterion === catalogCriterion && targetId === catalogTargetId)) {
    return NO_SORT_PARAMS;
  }

  return { orderBy: criterion, orderTargetId: targetId ?? undefined };
};

export const useCatalogSortParams = ({
  catalogId,
  canPersist,
}: UseCatalogSortParamsParams): UseCatalogSortParamsResult => {
  const catalogQuery = useGetCatalog({ id: catalogId }, { enabled: !!catalogId });
  const { getDefaultCatalogView, hasSettledUserPreferences } = useUserContextPreferences();

  const preference = canPersist ? getDefaultCatalogView() : null;

  const isSettled =
    (catalogQuery.isSuccess || catalogQuery.isError) && (!canPersist || hasSettledUserPreferences);

  if (!isSettled) {
    return { sortParams: NO_SORT_PARAMS, isSettled };
  }

  return {
    sortParams: resolveSortParams({
      preference,
      catalogCriterion: catalogQuery.data?.sectionSortCriterion,
      catalogTargetId: catalogQuery.data?.sortTarget?.id ?? null,
    }),
    isSettled,
  };
};
